import React, { createContext, JSX, ReactNode, useContext, useEffect, useLayoutEffect, useMemo } from 'react';
import { config } from '../config/environment';
import {
    readCachedBranding,
    readCachedHostContext,
    tenantIdFromToken,
    writeCachedBranding,
    writeCachedHostContext,
} from '../library/brandingCache';
import { logoAlt, logoSrc } from '../library/constants';
import { useDocumentIdentity } from '../library/useDocumentIdentity';
import { IPublicTenantContext } from '../library/models/publicTenantContext';
import { IBranding } from '../library/models/tenantBranding';
import { useReactQuery } from '../services/queryService';
import { services } from '../services/services';
import { applyBrandTokens, BrandTokens, deriveBrandTokens } from '../theme/brandTokens';
import { useAuth } from './AuthContext';

/**
 * How the app looks right now, and who it belongs to.
 *
 * Branding is applied through two channels, both driven from the same two colours:
 *
 *   * **CSS custom properties**, published onto `<html>`. Every `*.styles.ts` file points
 *     at `var(--ch-*)`, so setting them repaints the app with no React render at all.
 *   * **The MUI theme**, rebuilt by App from {@link BrandingContextValue.tokens}, because
 *     MUI parses its palette values and cannot take a custom property.
 *
 * <h2>Two ways to know which clinic, and they answer at different times</h2>
 *
 * 1. **The hostname**, via `GET /public/tenant-context`. Needs no session, so it is what
 *    dresses the page for a signed-out visitor to motioncare.ro. This is the newer half, and
 *    it is why the old rule -- *anything a signed-out visitor sees is the platform's* -- is now
 *    conditional: it still holds on carefull.ro and on any address nobody has registered,
 *    because those genuinely name no clinic. On a clinic's own domain it never did make sense.
 * 2. **The session**, via `GET /tenant-branding` and the token's `tid` claim. Still wins when
 *    present: it is the authenticated, always-current copy, and it is what makes an admin's own
 *    save repaint the app the moment it lands.
 *
 * They agree in every ordinary case -- `JwtAuthenticationFilter` refuses a token minted for a
 * clinic other than the one the hostname names, so a session on motioncare.ro is a Motion Care
 * session or it is nothing.
 */
export interface BrandingContextValue {
    /** What the API last said, or the cached copy, or null before either. */
    branding: IBranding | null;
    /** The clinic this hostname belongs to, or null on the platform's own address. */
    hostContext: IPublicTenantContext | null;
    /**
     * Whether this address is known to be the platform's, as opposed to merely not yet known.
     *
     * {@link hostContext} is null in both cases, so it cannot tell a visitor to carefull.ro from
     * one to motioncare.ro whose tenant-context request is still in flight. Anything that draws
     * Carefull's OWN content rather than just its colours needs the difference: drawing it on a
     * guess puts the platform's page on a clinic's front door for as long as the request takes.
     * True only once the API (or the cached copy of its answer) has said "platform".
     */
    isPlatformHost: boolean;
    /**
     * Whether the hostname's clinic is still being asked for, with no cached answer to stand in.
     * Settles to false on an error too, so a page waiting on it can say it failed rather than
     * spin for ever.
     */
    isHostPending: boolean;
    /**
     * The address the app was loaded from.
     *
     * Exposed because the public front-door queries key their cache on it -- a browser only
     * ever sees one origin, but a test rendering two clinics needs them to be two entries.
     */
    hostname: string;
    /** The derived palette. Falls back to the platform's, so this is never null. */
    tokens: BrandTokens;
    /** Absolute URL of the clinic's logo, or the Carefull mark when it has none. */
    logoSrc: string;
    /** The clinic's name when known, otherwise the platform's. */
    logoAlt: string;
    /**
     * The clinic's name to DRAW, when it is a clinic with no logo of its own.
     *
     * Null means "render {@link logoSrc}" -- either a real clinic logo, or the platform mark
     * on the platform's own address.
     *
     * It exists because the platform mark is the wrong fallback on a clinic's domain. Their
     * patients know the clinic and have never heard of Carefull, so putting Carefull's
     * wordmark above their appointments shows them a stranger's brand on an address they
     * typed themselves. The mail layer reached the same conclusion first and independently:
     * `EmailBrandingResolver.logoUrl` answers null rather than the platform mark, and the
     * template draws the clinic's name instead. A wordmark is a fair masthead; somebody
     * else's logo is not.
     */
    wordmark: string | null;
    /** True while the first fetch of a session is in flight and nothing is cached. */
    isLoading: boolean;
}

const BrandingContext = createContext<BrandingContextValue | undefined>(undefined);

export const useBranding = (): BrandingContextValue => {
    const context = useContext(BrandingContext);

    if (!context) {
        throw new Error('useBranding must be used inside BrandingProvider');
    }

    return context;
};

/**
 * The image path the API returns is relative to the API, which the edge serves under `/api` on
 * this same hostname. Resolved here rather than on the server so the stored value stays
 * host-independent -- which is what lets one row serve the clinic's own domain, the platform's,
 * and a local dev server without knowing about any of them.
 *
 * Exported because the branding editor resolves the watermark with it too.
 *
 * The ETag rides along as `?v=`. The path carries no version -- it is
 * `/tenant-branding/{id}/logo` for the life of the clinic -- and the image is served with
 * `max-age=300`, so without this an admin who re-crops their logo keeps seeing the old one
 * for five minutes and reasonably concludes the upload did nothing. The server ignores the
 * parameter; only the browser's cache key moves, and the ETag underneath still answers 304
 * for an image that really has not changed.
 */
export const absoluteBrandingUrl = (
    path: string | null | undefined,
    etag: string | null | undefined
): string | null => (path ? `${config.apiBaseUrl}${path}${etag ? `?v=${etag}` : ''}` : null);

/** Read once per load: it cannot change without a navigation, which remounts everything. */
const currentHostname = (): string =>
    typeof window === 'undefined' ? '' : window.location.hostname;

export const BrandingProvider = ({ children }: { children: ReactNode }): JSX.Element => {
    const { token } = useAuth();

    const hostname: string = useMemo(currentHostname, []);

    /**
     * Which clinic this ADDRESS belongs to.
     *
     * Fetched unconditionally, including while signed in and including on the platform's own
     * hostname, because "no clinic here" is the answer that tells the app to wear the
     * platform's colours. It is public, small and cached for a minute on both sides.
     */
    const { data: hostData, isLoading: isHostLoading } = useReactQuery<IPublicTenantContext>(
        services.publicTenant.getTenantContext(hostname),
    );

    /**
     * Which clinic this SESSION belongs to, and the question this provider used to ask alone.
     *
     * Falls back to the stored token because auth state hydrates in an effect: on the first
     * render of a reload `token` is still null, and deferring to it would paint a frame of the
     * platform over an already signed-in clinic -- the flash the cache exists to prevent.
     *
     * A signed-out visitor has no token, and the platform owner's carries no `tid` because they
     * belong to no clinic, so both come out null and neither asks the API for branding it would
     * refuse.
     */
    const sessionToken: string | null = token ?? localStorage.getItem('authToken');
    const tenantId: number | null = useMemo(
        () => tenantIdFromToken(sessionToken),
        [sessionToken],
    );

    const wantsBranding: boolean = tenantId !== null;

    const { data, isLoading } = useReactQuery<IBranding>(
        services.tenantBranding.getBranding(tenantId),
        { enabled: wantsBranding },
    );

    /**
     * Read straight off the query, never mirrored into state.
     *
     * It used to be a `useState` fed by an effect on `data`, and that is what left a second
     * sign-in within one page session wearing the platform's colours. React Query holds this
     * query for `gcTime` and structurally shares an unchanged response, so signing out and
     * back in resolved to *the same object reference*: the effect never re-ran, and the
     * state stayed on the null that signing out had set. Only a reload moved it, because
     * only a reload empties the query cache. Deriving has no such gap -- and it is what
     * makes the admin's own save repaint the app the moment it lands.
     *
     * The cached copy stands in until the first response of a session arrives, so a reload
     * paints the clinic's colours on its first frame rather than a round trip later.
     */
    const cached: IBranding | null = useMemo(() => readCachedBranding(tenantId), [tenantId]);
    const branding: IBranding | null = wantsBranding ? (data ?? cached) : null;

    /** The same trick for the host half, and the reason a signed-out visit is never unbranded. */
    const cachedHost: IPublicTenantContext | null = useMemo(
        () => readCachedHostContext(hostname),
        [hostname],
    );

    const resolvedHost: IPublicTenantContext | null = hostData ?? cachedHost;

    const hostContext: IPublicTenantContext | null = useMemo(
        () => (resolvedHost && !resolvedHost.platform ? resolvedHost : null),
        [resolvedHost],
    );

    const isPlatformHost: boolean = resolvedHost?.platform === true;
    const isHostPending: boolean = isHostLoading && resolvedHost === null;

    // Keyed by the same tenant the response was fetched for, never by whoever is stored
    // now, so a sign-out mid-flight cannot file one clinic's colours under another's id.
    useEffect(() => {
        if (data && tenantId !== null) {
            writeCachedBranding(tenantId, data);
        }
    }, [data, tenantId]);

    // Cached under the hostname it was fetched for, and including the platform answer: a
    // visitor to carefull.ro should not get a frame of the last clinic they visited either.
    useEffect(() => {
        if (hostData && hostname) {
            writeCachedHostContext(hostname, hostData);
        }
    }, [hostData, hostname]);

    /**
     * The session's colours win, and the hostname's stand in until there are any.
     *
     * That order matters in exactly one moment: the admin of a clinic saving a new colour. The
     * session copy updates immediately, while the public one is a minute stale on both sides of
     * the wire -- so preferring the host's would show them their old colours until the cache
     * expired.
     */
    const tokens: BrandTokens = useMemo(
        () => deriveBrandTokens(
            branding?.primaryColor ?? hostContext?.primaryColor,
            branding?.secondaryColor ?? hostContext?.secondaryColor,
        ),
        [branding?.primaryColor, branding?.secondaryColor,
            hostContext?.primaryColor, hostContext?.secondaryColor],
    );

    // Layout effect, not effect: this writes the custom properties every styles file reads,
    // and doing it after paint would be the flash all over again.
    useLayoutEffect(() => {
        applyBrandTokens(tokens);
    }, [tokens]);

    /**
     * The tab, too -- not just the page.
     *
     * Driven from the HOST half rather than from the session, deliberately: the tab belongs to
     * the address, so it should say Motion Care to a signed-out visitor on motioncare.ro and go
     * on saying it after they sign out again.
     *
     * <h2>The favicon first, the logo behind it</h2>
     *
     * The icon was the logo alone until a clinic could upload a favicon of its own, and the
     * fallback is what keeps that true for every clinic that has not. It is not a stopgap: a
     * favicon is optional on purpose, and a clinic whose mark is already square has no reason
     * to upload the same picture twice.
     *
     * The order is the only one that works. Preferring the logo would make the favicon card
     * do nothing, and dropping the fallback would take the icon away from every clinic on the
     * day this shipped.
     */
    useDocumentIdentity(
        hostContext?.displayName,
        absoluteBrandingUrl(hostContext?.faviconUrl, hostContext?.faviconEtag)
            ?? absoluteBrandingUrl(hostContext?.logoUrl, hostContext?.logoEtag),
    );

    /** Whether a real clinic logo was found, as opposed to falling back to the platform mark. */
    const hasOwnLogo: boolean = Boolean(branding?.logoUrl ?? hostContext?.logoUrl);

    const value: BrandingContextValue = useMemo(
        () => ({
            branding,
            hostContext,
            isPlatformHost,
            isHostPending,
            hostname,
            tokens,
            logoSrc: absoluteBrandingUrl(branding?.logoUrl, branding?.logoEtag)
                ?? absoluteBrandingUrl(hostContext?.logoUrl, hostContext?.logoEtag)
                ?? logoSrc,
            logoAlt: branding?.displayName ?? hostContext?.displayName ?? logoAlt,
            wordmark: hasOwnLogo ? null : (branding?.displayName ?? hostContext?.displayName ?? null),
            isLoading: wantsBranding && isLoading && branding === null,
        }),
        [branding, hostContext, isPlatformHost, isHostPending, hostname, hasOwnLogo, tokens, wantsBranding, isLoading],
    );

    return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
};
